import { SimpleFilter } from './Filters';
import { ContactFilterRequest } from './requests/ContactFilterRequest';
import {
	ActionItemPriority,
	AIContactReference,
	BaseIdModel,
	BaseResourceModel,
	FilterOperator,
	FilterRequestBase,
	IActor,
	IBaseApiTypeModel,
	IBulkContactsRequest,
	IEmailContentGenerationRequest,
	IFileAttachment,
	IGoogleBusinessReview,
	IRawRichTextContentState,
	ISystemJob,
	MeetingPrepDeliveryTiming,
	UserReference,
} from './index';

export enum AIConversationType {
	Default = 'Default',
	EmailContentGeneration = 'EmailContentGeneration',
	SocialMediaContentGeneration = 'SocialMediaContentGeneration',
	Translation = 'Translation',
	GoogleReviewReply = 'GoogleReviewReply',
	MediaKeywordGeneration = 'MediaKeywordGeneration',
	BlogGeneration = 'BlogGeneration',
	ContactClassification = 'ContactClassification',
	SalesLiveCoaching = 'SalesLiveCoaching',
	EmailLeadParser = 'EmailLeadParser',
	SalesPostCallCoaching = 'SalesPostCallCoaching',
	SalesDemoCallCoaching = 'SalesDemoCallCoaching',
	SiteWide = 'SiteWide',
	Admin = 'Admin',
	SalesPracticeCall = 'SalesPracticeCall',
	ImageGeneration = 'ImageGeneration',
	AccountOnboarding = 'AccountOnboarding',
	SuccessMeetingPrep = 'SuccessMeetingPrep',
	Custom = 'Custom',
	ContentCalendar = 'ContentCalendar',
	CustomAgentBuilder = 'CustomAgentBuilder',
	SurveyBuilder = 'SurveyBuilder',
}

export enum AIAgentTemplateScope {
	Account = 'Account',
	System = 'System',
	User = 'User',
}

export enum AIAgentTemplateFilterProperty {
	Scope = 'Scope',
	ConversationType = 'ConversationType',
	Name = 'Name',
	IsActive = 'IsActive',
	Id = 'Id',
}

export interface AIAgentTemplateFilterCriteria {
	criteria?: AIAgentTemplateFilterCriteria[];
	op?: FilterOperator;
	property?: AIAgentTemplateFilterProperty;
	value?: string;
	values?: Record<string, string>;
}

export interface AIAgentTemplateFilter {
	criteria?: AIAgentTemplateFilterCriteria[];
	op?: FilterOperator;
}

export enum AIChatRole {
	Assistant = 'Assistant',
	System = 'System',
	User = 'User',
}

export interface AIConversation extends BaseResourceModel {
	participants: AIConversationParticipant[];
	conversationType?: AIConversationType;
	title?: string;
	/**
	 * The AI agent template this conversation ran on. Authoritative, unlike `agentTemplateName`, and
	 * the value `AIConversationFilterProperty.AgentTemplateId` filters against.
	 */
	agentTemplateId?: string;
	/**
	 * Display name for `agentTemplateId`, denormalized so lists need no per-row lookup. Best-effort:
	 * omitted when the template was deleted or is unreadable even though the id is present, so never
	 * render this without falling back to the id.
	 */
	agentTemplateName?: string;
	resourceReferences?: IAIConversationResourceReference[];
}

export interface IAIConversationResourceReference {
	resourceType?: string;
	resourceId?: string;
	label?: string;
}

interface AIConversationParticipant {
	id: string;
}

export interface AIUserParticipant extends AIConversationParticipant {
	userId?: string;
	reference?: UserReference;
}

export interface AIAssistantParticipant extends AIConversationParticipant {
	assistantName?: string;
}

export type AIChatContent = IBaseApiTypeModel;

export interface AITextContent extends AIChatContent {
	_type: 'AITextContent';
	text: string;
}

export interface AIToolContent extends AIChatContent {
	_type: 'AIToolContent';
	toolCall: AIToolCall;
}

export interface AIActionContent extends AIChatContent {
	_type: 'AIActionContent';
	action?: AIAction;
}

export interface AIFileContent extends AIChatContent {
	_type: 'AIFileContent';
	fileAttachmentId: string;
}

export interface AIToolCall extends IBaseApiTypeModel {
	id: string;
	toolName: string;
	propertyName: string;
	value: string;
	toolOutputJson?: string;
	toolOutputType?: string;
}

/**
 * Audience information for pre-populating campaign recipients.
 * Returned by the AI when it searches for contacts by tags and generates an email.
 */
export interface ContactAudienceInfo {
	/** Tag-based filters for dynamic audience targeting */
	tagFilters?: SimpleFilter[];
	/** Native contact filter request from AI (preferred over tagFilters when present) */
	contactFilterRequest?: IBulkContactsRequest;
	/** Explicit contact IDs (fallback for small/specific audiences, may be truncated for large audiences) */
	contactIds?: string[];
	/** Total number of contacts in the audience */
	totalCount: number;
	/** Human-readable summary, e.g., "47 contacts with VIP tag" */
	searchSummary?: string;
	/** Original search context for debugging/display */
	searchContext?: string;
}
export interface ContentCalendarResourceToolCall {
	id: string;
	calendarId: string;
	resourceId: string;
}

export interface ContentCalendarEntryToolCall extends AIToolCall {
	entryId: string;
	calendarId: string;
	scheduledDate: string;
	templateName: string;
	contentType: string;
	source: string;
	text: string;
	reason?: string;
	templateId?: string;
}

export interface EmailToolCall extends AIToolCall {
	subject: string;
	body: IRawRichTextContentState;
	signature?: IRawRichTextContentState;
	/** Audience information for pre-populating campaign recipients */
	audienceInfo?: ContactAudienceInfo;
}

export interface ContactToolCall extends AIToolCall {
	contactId: string;
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber: string;
	company: string;
}

export interface SocialMediaToolCall extends AIToolCall {
	content: IRawRichTextContentState;
}

export interface GoogleReviewReplyToolCall extends AIToolCall {
	reply: string;
}

export interface ActionItemsToolCall extends AIToolCall {
	actionItems: IEmailExecProposedActionItem[];
}

/** One action item the assistant created, updated, or completed on the user's behalf. */
export interface ActionItemToolCall extends AIToolCall {
	_type: 'ActionItemToolCall';
	actionItemId: string;
	description: string;
	dueDate?: string;
	hasDueTime: boolean;
	priority?: ActionItemPriority;
	assigneeId?: string;
	isCompleted: boolean;
	contactId?: string;
	contactName?: string;
}

export interface BlogToolCall extends AIToolCall {
	title: string;
	content: IRawRichTextContentState;
}

export interface ContactListToolCall extends AIToolCall {
	contacts: AIContactReference[];
	filters: SimpleFilter[];
	/** Native contact filter request from AI (preferred over filters when present) */
	contactFilterRequest?: IBulkContactsRequest;
	totalCount: number;
}

export interface NavigationMetadata {
	id: string;
	entityType: string;
	parentNavigationMetadata?: NavigationMetadata;
}

export interface NavigationToolCall extends AIToolCall {
	navigationItems: NavigationMetadata[];
	totalCount: number;
}

/** `lev-website-change`: a page of the account's website changed; the preview beside the chat should reload and show it. */
export interface WebsiteChangeToolCall extends AIToolCall {
	pageId: string;
	pageName?: string;
	/** Root-relative path (`/services/roofing`), also the draft-preview path. */
	pagePath?: string;
	/** What changed, in the user's terms. */
	summary: string;
	elementIndex?: number;
	fieldPath?: string;
}

export interface MediaContentToolCall extends AIToolCall {
	systemJobId?: string;
	fileAttachments?: IFileAttachment[];
}

export interface MeetingSchedulingToolCall extends AIToolCall {
	meetingId: string;
	meetingConfigId: string;
	meetingName: string;
	duration: string;
	locationType: string;
}

export interface ConfirmationItem {
	label: string;
	detail?: string;
}

export interface ConfirmationToolCall extends AIToolCall {
	_type: 'ConfirmationToolCall';
	title?: string;
	summary?: string;
	items?: ConfirmationItem[];
	confirmLabel?: string;
	declineLabel?: string;
	revision?: string;
	state?: string;
}

export interface QuestionCardOption {
	id: string;
	label: string;
	description?: string;
}

export interface QuestionCardQuestion {
	id: string;
	prompt: string;
	multiSelect: boolean;
	options: QuestionCardOption[];
	/** Always one of the question's options. */
	recommendedOptionId?: string;
}

/** `lev-question`: a card of 1–4 questions. The answer goes back as this tool call's state through a ToolCallResponseAction. */
export interface QuestionToolCall extends AIToolCall {
	_type: 'QuestionToolCall';
	header?: string;
	composerPlaceholder?: string;
	allowSkip: boolean;
	questions: QuestionCardQuestion[];
	state?: string;
}

/** `lev-ai-campaign-audience`: the composer chat saved the draft's audience. */
export interface AICampaignAudienceToolCall extends AIToolCall {
	_type: 'AICampaignAudienceToolCall';
	campaignId: string;
	audience?: ContactFilterRequest;
	totalContacts: number;
	totalEmails: number;
}

/** `lev-ai-campaign-change`: the composer chat changed the draft's brief, sender or samples, named by `propertyName`. */
export interface AICampaignChangeToolCall extends AIToolCall {
	_type: 'AICampaignChangeToolCall';
	campaignId: string;
}

/** `lev-ai-campaign-ready-to-schedule`: the draft is complete and the user asked to schedule it. */
export interface AICampaignReadyToScheduleToolCall extends AIToolCall {
	_type: 'AICampaignReadyToScheduleToolCall';
	campaignId: string;
	totalContacts: number;
	totalEmails: number;
}

export enum AIChatMessageFeedback {
	ThumbsUp = 'ThumbsUp',
	ThumbsDown = 'ThumbsDown',
}

export interface AIChatMessageFeedbackInfo {
	value: AIChatMessageFeedback;
	submittedBy: string;
	submittedAt: Date;
}

export interface AIChatMessage extends BaseResourceModel {
	participantId?: string;
	aiConversationId?: string;
	role?: AIChatRole;
	contents?: AIChatContent[];
	text?: string;
	fileAttachments?: IFileAttachment[];
	feedback?: AIChatMessageFeedbackInfo;
	/** Optional per-response usage metadata; populated by the backend for assistant messages when available. */
	inputTokenCount?: number;
	/** Optional per-response usage metadata; populated by the backend for assistant messages when available. */
	outputTokenCount?: number;
	/** Optional per-response usage metadata; populated by the backend for assistant messages when available. */
	totalTokenCount?: number;
	/** Optional per-response usage metadata; populated by the backend for assistant messages when available. */
	cachedInputTokenCount?: number;
	/** Optional per-response usage metadata; populated by the backend for assistant messages when available. */
	reasoningTokenCount?: number;
	/** Optional per-response usage metadata; populated by the backend for assistant messages when available. */
	finishReason?: string;
	/** Optional per-response usage metadata; populated by the backend for assistant messages when available. */
	model?: string;
	/** Optional per-response usage metadata; populated by the backend for assistant messages when available. */
	provider?: string;
}

export type AIContext = IBaseApiTypeModel;

export interface ResourceAIContext extends AIContext {
	_type: 'ResourceAIContext';
	resourceType: string;
	resourceId: string;
}

/** Scopes a turn to one element of a CMS page. Sent alongside the page's ResourceAIContext, never instead of it. */
export interface WebsiteSelectionAIContext extends AIContext {
	_type: 'WebsiteSelectionAIContext';
	pageId: string;
	/** Zero-based index into the page's element list. */
	elementIndex: number;
	/** Set only when the selection is finer than the whole element, e.g. `text` or `items[0].answer`. */
	fieldPath?: string;
}

export interface EmailAIContext extends AIContext {
	_type: 'EmailAIContext';
	subject: string;
	body: IRawRichTextContentState;
}

export interface SocialMediaAIContext extends AIContext {
	_type: 'SocialMediaAIContext';
	content: IRawRichTextContentState;
}

export interface GoogleReviewAIContext extends AIContext {
	type: 'GoogleReviewAIContext';
	review: IGoogleBusinessReview;
	reviewedCompanyName: string;
	reply: string;
}

export interface BlogAIContext extends AIContext {
	type: 'BlogAIContext';
	title: string;
	content: IRawRichTextContentState;
}

/** The blog post a featured image is being generated for. */
export interface BlogImageAIContext extends AIContext {
	_type: 'BlogImageAIContext';
	content: IRawRichTextContentState;
}

export interface SalesLeadAIContext extends AIContext {
	_type: 'SalesLeadAIContext';
	companyId: string;
}

export interface MediaAIContext extends AIContext {
	_type: 'MediaAIContext';
	fileAttachmentIds?: string[];
	urls?: string[];
	base64Images?: string[];
}

export interface AutomationFlowAIContext extends AIContext {
	_type: 'AutomationFlowAIContext';
	canvasName: string;
	description: string;
	nodes: {
		id: string;
		label: string;
		type?: string;
		metadata?: any;
	}[];
	edges: {
		id: string;
		source: string;
		target: string;
		type?: string;
		animated: boolean;
	}[];
}

export interface AIChatRequest extends IBaseApiTypeModel {
	contexts?: AIContext[];
	/**
	 * When true, the stream includes intermediate Progress events describing
	 * what the agent is doing during long-running tool calls. Default false;
	 * backend ignores when no plugin tool has a ProgressLabel attribute.
	 */
	includeProgress?: boolean;
	/**
	 * When true, the backend injects the agent's configured starter prompt
	 * on the first stream of a brand-new conversation. The agent owns the
	 * prompt text — the FE no longer assembles a System-role bootstrap.
	 * Only valid on the first stream; rejected (400) if the conversation
	 * already has messages or the agent returns no starter prompt.
	 */
	autoStart?: boolean;
}

export interface AIMessageRequest extends AIChatRequest {
	chatMessages?: AIChatMessage[];
}

export interface AIActionRequest extends AIChatRequest {
	action?: AIAction;
}

export interface AIAction extends BaseIdModel {
	name?: string;
	description?: string;
}

export interface EmailContentGenerationAction extends AIAction {
	contentGenerationRequest?: IEmailContentGenerationRequest;
}

export interface GoogleReviewReplyAction extends AIAction {
	reviewId?: string;
}

export interface EmailExecBriefAction extends AIAction {
	startDate?: string;
	endDate?: string;
}

export interface ContentSettingsIntakeAction extends AIAction {
	_type: 'ContentSettingsIntakeAction';
	websiteUrl?: string;
}

export interface CoachPostCallSummaryAction extends AIAction {
	_type: 'CoachPostCallSummaryAction';
	transcript?: string;
}

export interface CoachRecentCallsReviewAction extends AIAction {
	_type: 'CoachRecentCallsReviewAction';
	/** Server default 25, clamped to 3–25. */
	maxCalls?: number;
}

export interface TysonNextContactStartedAction extends AIAction {
	_type: 'TysonNextContactStartedAction';
	contactName?: string;
}

export interface TysonPracticeCallStartedAction extends AIAction {
	_type: 'TysonPracticeCallStartedAction';
	contactName?: string;
}

export enum ContentConversionType {
	Email = 'Email',
	Blog = 'Blog',
	Social = 'Social',
}

export interface ContentConversionAction extends AIAction {
	_type: 'ContentConversionAction';
	fromContentType: ContentConversionType;
	toContentType: ContentConversionType;
	contentId?: string;
	content?: IRawRichTextContentState;
}

export interface ToolCallResponseAction extends AIAction {
	_type: 'ToolCallResponseAction';
	messageId?: string;
	toolCallId?: string;
	response?: string;
}

export interface ImageBrandingAction extends AIAction {
	_type: 'ImageBrandingAction';
	sourceFileAttachmentIds: string[];
	templateId?: string;
	priorJobId?: string;
	keptResultFileAttachmentIds?: string[];
}

export enum AIConversationFilterProperty {
	ConversationType,
	CreatorId,
	AgentTemplateId,
	Category,
	AssistantName,
	CreationDate,
	Title,
	ResourceId,
}

export interface AIConversationFilterCriteria {
	criteria: AIConversationFilterCriteria[];
	op?: FilterOperator;
	property: AIConversationFilterProperty;
	value?: string;
	values?: Record<string, string>;
}

export type AIConversationFilter = FilterRequestBase<AIConversationFilterProperty, FilterOperator>;

export interface AIConversationStarter {
	title: string;
	subTitle?: string;
	chatMessages: AIChatMessage[];
}

export interface AIChatStreamResponse {
	id: string;
	isDelta: boolean;
}

interface AIMessage {
	text: string;
}
export interface AIChatStreamMessageResponse extends AIChatStreamResponse {
	message: AIMessage;
}

export interface AIChatStreamToolResponse extends AIChatStreamResponse {
	toolCall: AIToolCall;
}

// Progress events are transport-only and don't carry the base id/isDelta fields on the wire,
// so they're omitted from the inherited shape and re-declared as optional.
export interface AIChatStreamProgressResponse extends Omit<AIChatStreamResponse, 'id' | 'isDelta'> {
	id?: string;
	isDelta?: boolean;
	stage: string;
	message: string;
}

// Anonymous Access Settings for AI Agent Templates
export interface IAnonymousAccessSettings {
	enabled: boolean;
	allowedOrigins?: string[];
}

export enum AIConversationCategory {
	System = 'System',
	ContentGeneration = 'ContentGeneration',
	Sales = 'Sales',
	Public = 'Public',
}

// AI Agent Management Types
export interface IAIAgentTemplate extends BaseResourceModel {
	name: string;
	description?: string;
	isActive: boolean;
	objectivePrompt?: string;
	technicalPrompt?: string;
	vectorStore?: IAIVectorStore;
	model?: string;
	attachments?: IAIAgentTemplateAttachment[];
	conversationType?: AIConversationType;
	selectors?: Record<string, string>;
	variables?: Record<string, string>;
	actor?: IActor;
	scope?: AIAgentTemplateScope;
	agentAvatarImage?: IFileAttachment;
	conversationStarters?: AIConversationStarter[];
	anonymousAccess?: IAnonymousAccessSettings;
	capabilities?: AICapability[];
	category?: AIConversationCategory;
}

export type DayOfWeekName = 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';

export interface IWeeklyScheduleSlot {
	dayOfWeek: DayOfWeekName;
	hour: number;
	minute: number;
}

export interface IEmailExecMailboxSources {
	includeAllUsers?: boolean;
	userIds?: string[];
}

export interface EmailExecAssistantTemplate extends IAIAgentTemplate {
	monitoredKeywords?: string[];
	ignoreNoiseNotes?: string;
	scheduleEnabled?: boolean;
	scheduleSlots?: IWeeklyScheduleSlot[];
	mailboxSources?: IEmailExecMailboxSources;
}

/**
 * A user has at most one, enforced server-side, so a second create returns 409. `objectivePrompt` is
 * optional guidance — a template without one still runs the agent's default brief structure.
 */
export interface MeetingPrepTemplate extends IAIAgentTemplate {
	scheduleEnabled?: boolean;
	sendEmail?: boolean;
	sendHourLocal?: number;
	deliveryTiming?: MeetingPrepDeliveryTiming;
	weekendDelivery?: boolean;
	includeInternalAttendees?: boolean;
	/** ISO-8601 UTC, read-only — the server ignores it on write and carries the stored value forward. */
	lastRunDate?: string;
}

export interface KeepInTouchTemplate extends IAIAgentTemplate {
	audiencePrompt: string;
	scheduleEnabled?: boolean;
	lastRunDate?: string;
	lastJobId?: string;
}

export interface IEmailExecSentimentItem {
	contactId?: string;
	contactName?: string;
	senderEmail?: string;
	sentiment?: string;
	summary?: string;
	trendNote?: string;
}

export interface IEmailExecWatchlistItem {
	contactId?: string;
	contactName?: string;
	senderEmail?: string;
	originalAsk?: string;
	sourceMessageId?: string;
	daysWaiting?: number;
}

export interface IEmailExecFyiItem {
	summary?: string;
	contactId?: string;
	contactName?: string;
	senderEmail?: string;
	sourceMessageId?: string;
}

export interface IEmailExecDraftReply {
	heading?: string;
	contactId?: string;
	contactName?: string;
	toEmail?: string;
	subject?: string;
	body?: string;
	replyToMessageId?: string;
	replyToSubject?: string;
}

export interface IEmailExecProposedActionItem {
	text?: string;
	contactId?: string;
	contactName?: string;
	senderEmail?: string;
	suggestedDueDate?: string;
	sourceMessageId?: string;
}

export const EMAIL_EXEC_DIGEST_RESOURCE_TYPE = 'EmailExecDigest';

export interface IEmailExecDigest {
	id?: string;
	runDate?: string;
	triggerType?: string;
	briefMarkdown?: string;
	windowStart?: string;
	windowEnd?: string;
	messagesScanned?: number;
	messagesMatched?: number;
	clientSentiments?: IEmailExecSentimentItem[];
	watchlist?: IEmailExecWatchlistItem[];
	fyiItems?: IEmailExecFyiItem[];
	draftReplies?: IEmailExecDraftReply[];
	proposedActionItems?: IEmailExecProposedActionItem[];
	/** Mailboxes this brief actually read, owner included. Absent when it covered only the owner's own. */
	sourceUserIds?: string[];
}

export type EmailExecBriefRunStatus = 'Queued' | 'InProgress' | 'Success' | 'Failed';

export enum AgentRunTrigger {
	Chat = 'Chat',
	Scheduled = 'Scheduled',
}

export interface IEmailExecBriefSystemJob extends ISystemJob {
	templateId?: string;
	runTrigger?: AgentRunTrigger;
	status?: EmailExecBriefRunStatus;
	digestId?: string;
	aiConversationId?: string;
	errorMessage?: string;
	processingCompletedUtc?: string;
}

export const MEETING_PREP_BRIEF_RESOURCE_TYPE = 'MeetingPrepBrief';

export type MeetingPrepBriefStatus = 'Success' | 'Failed' | 'Skipped';

export interface IAIDocumentArtifact {
	fileAttachmentId?: string;
	fileName?: string;
	mimeType?: string;
	documentType?: string;
}

export interface IMeetingPrepBriefAttendee {
	email?: string;
	displayName?: string;
	contactId?: string;
	companyName?: string;
	isOrganizer?: boolean;
}

export interface IMeetingPrepBrief {
	id?: string;
	runId?: string;
	meetingId?: string;
	meetingSummary?: string;
	meetingStartTime?: string;
	meetingEndTime?: string;
	durationMinutes?: number;
	status?: MeetingPrepBriefStatus;
	prepSummary?: string;
	prompt?: string;
	attendees?: IMeetingPrepBriefAttendee[];
	artifacts?: IAIDocumentArtifact[];
	errorMessage?: string;
	creationDate?: string;
}

export interface IAIAgentTemplateAttachment {
	fileAttachment: IFileAttachment;
	vectorStoreFileId?: string;
}

export interface IAIVectorStore {
	id?: string;
	_type: string;
}

export interface IOpenAIVectorStore extends IAIVectorStore {
	_type: 'OpenAIVectorStore';
	storeId?: string;
}

// Form data interface for editing
export interface IAIAgentTemplateFormData {
	name: string;
	description: string;
	objectivePrompt: string;
	technicalPrompt: string;
	model: string;
	conversationType: string;
	selectors: Record<string, string>;
	variables: Record<string, string>;
	attachments: IAIAgentTemplateAttachment[];
	agentAvatarImage?: IFileAttachment | null;
	conversationStarters?: AIConversationStarter[];
	anonymousAccess?: IAnonymousAccessSettings;
	capabilities?: AICapability[];
	scope?: AIAgentTemplateScope;
	category?: AIConversationCategory;
}

// Group interface for organizing templates by selector hash
export interface IAIAgentTemplateGroup {
	hash: string;
	templates: IAIAgentTemplate[];
	selectors: Record<string, string>;
}

// AI Capability Types
export interface CapabilityResourceConfiguration {
	/**
	 * Polymorphic discriminator. Entries without a `_type` (or with
	 * 'CapabilityResourceConfiguration') reference an individual user's
	 * MeetingConfig; 'TeamMeetingResourceConfiguration' entries reference a
	 * TeamMeetingConfig (round-robin team meeting link).
	 */
	_type?: 'CapabilityResourceConfiguration' | 'TeamMeetingResourceConfiguration';
	resourceId: string;
	templateId?: string;
	description?: string;
}

export const isTeamMeetingResource = (resource: CapabilityResourceConfiguration): boolean =>
	resource._type === 'TeamMeetingResourceConfiguration';

export interface AICapability {
	id?: string;
	templateId?: string;
	name?: string;
	description?: string;
	isActive?: boolean;
	pluginTypes?: string[];
	aiActionTypes?: string[];
	customInstructions?: string;
	_type?: string;
}

export interface CustomSurveyCapability extends AICapability {
	_type: 'CustomSurveyCapability';
	resources?: CapabilityResourceConfiguration[];
}

export interface EventRegistrationCapability extends AICapability {
	_type: 'EventRegistrationCapability';
	includeAllUpcomingEvents?: boolean;
	resources?: CapabilityResourceConfiguration[];
}

export interface MeetingCapability extends AICapability {
	_type: 'MeetingCapability';
	resources?: CapabilityResourceConfiguration[];
}

export interface WebSearchCapability extends AICapability {
	_type: 'WebSearchCapability';
	allowedDomains?: string[];
}
